import { Component, OnInit, ViewChild } from '@angular/core';
import { ModalController, NavParams, AlertController } from '@ionic/angular';

import { SlackChannel } from 'src/models';
import { Entity } from 'src/models/entities';
import { EntitiesService } from 'src/services/entities.service';
import { EscalationTypesService } from 'src/services/escalation-types.service';
import { MessageService } from 'src/services/message.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';
import { SlackService } from 'src/services/slack.service';

@Component({
  selector: 'app-edit-escalation-type',
  templateUrl: './edit-escalation-type.component.html',
  styleUrls: ['./edit-escalation-type.component.scss'],
})
export class EditEscalationTypeComponent implements OnInit {
  @ViewChild('nameInput', { static: false }) nameInput;

  id?: string;
  projectId: string;
  isLoading = false;

  name: string;
  description: string;
  slackChannelId?: string;
  slackChannel?: SlackChannel;

  assigneeSelector: number = 0; // 0 = Disabled, 1 = Optional, 2 = Required
  prioritySelector: number = 0; // using same mapping as ticket types (disabled by default)

  assigneeEntities: { id: string; name: string; type: string; photo?: string }[] = [];

  constructor(
    private navParams: NavParams,
    private modalCtrl: ModalController,
    private escalationTypesService: EscalationTypesService,
    private msgSrvc: MessageService,
    private searchablePopoverService: SearchablePopoverService,
    private slackService: SlackService,
    private entitiesService: EntitiesService,
    private alertCtrl: AlertController,
  ) {
    this.id = this.navParams.get('id');
    this.projectId = this.navParams.get('projectId');
  }

  ngOnInit(): void {
    if (this.id) {
      this.loadEscalationType();
    } else {
      setTimeout(() => this.nameInput?.setFocus(), 500);
    }
  }

  async loadEscalationType() {
    try {
      this.isLoading = true;
      const escalationType = await this.escalationTypesService.getById(this.id);

      this.name = escalationType.name;
      this.description = escalationType.description;
      this.slackChannelId = escalationType.slackChannelId;
      this.assigneeSelector = escalationType.assigneeSelector ?? 0;
      this.prioritySelector = escalationType.prioritySelector ?? 0;

      if (escalationType.assigneeSelectorIds?.length) {
        const result = await this.entitiesService.list({
          limit: null,
          where: [{ field: 'id', operator: 'in', value: escalationType.assigneeSelectorIds }],
        });
        this.assigneeEntities = result.data.map((e: Entity) => ({
          id: e.id,
          name: e.name,
          type: e.type,
          photo: e.photo,
        }));
      } else {
        this.assigneeEntities = [];
      }

      if (this.slackChannelId) {
        this.slackChannel = await this.slackService.getChannel(this.slackChannelId);
      }
    } catch (error) {
      this.msgSrvc.showError(error);
    } finally {
      this.isLoading = false;
    }
  }

  onSlackChannelChanged(channel?: SlackChannel) {
    this.slackChannelId = channel?.id;
    this.slackChannel = channel;
  }

  trackByAssigneeEntityId(_index: number, entity: { id: string }) {
    return entity.id;
  }

  addAssigneeEntity() {
    this.searchablePopoverService.entities({
      event,
      selectedEntityId: undefined,
      callback: (id: string, name: string, entity: any) => {
        if (!this.assigneeEntities.find((e) => e.id === id)) {
          this.assigneeEntities.push({ id, name, type: entity.type, photo: entity?.photo });
        }
      },
    });
  }

  removeAssigneeEntity(entity) {
    this.assigneeEntities = this.assigneeEntities.filter((e) => e !== entity);
  }

  async submit() {
    if (!this.name) {
      await this.msgSrvc.show('Whoops! Please enter a name.');
      return;
    }

    this.isLoading = true;

    const payload: any = {
      name: this.name,
      description: this.description,
      slackChannelId: this.slackChannelId,
      assigneeSelector: this.assigneeSelector,
      assigneeSelectorIds: this.assigneeEntities.map((e) => e.id),
      prioritySelector: this.prioritySelector,
      projectId: this.projectId,
    };

    try {
      if (this.id) {
        await this.escalationTypesService.updatePartial(this.id, payload);
        await this.msgSrvc.show('Escalation type updated!');
      } else {
        await this.escalationTypesService.create(payload);
        await this.msgSrvc.show('Escalation type added!');
      }

      this.dismiss();
    } catch (error) {
      await this.msgSrvc.showError(error);
    } finally {
      this.isLoading = false;
    }
  }

  async delete() {
    const alert = await this.alertCtrl.create({
      header: 'Delete escalation type?',
      subHeader: `This can't be undone.`,
      buttons: [
        { text: 'Nevermind' },
        {
          text: 'Delete',
          handler: async () => {
            try {
              this.isLoading = true;
              await this.escalationTypesService.delete(this.id);
              this.isLoading = false;
              this.dismiss();
            } catch (e) {
              this.isLoading = false;
              this.msgSrvc.showError(e);
            }
          },
        },
      ],
    });

    alert.present();
  }

  dismiss() {
    this.modalCtrl.dismiss();
  }
}
